import React, { useState, useContext } from 'react';
import { Button, DialogActions, Grid, TextField, IconButton, Paper, MenuItem, CircularProgress } from '@mui/material';
import styles from './styles.module.css';
import CloseIcon from '@mui/icons-material/Close';
import { AlertMessagesContext } from 'react-alert-messages';
import TableService from 'services/TableService';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from '@mui/material/styles';
import NumberField from 'components/library/NumberField';
import { useTranslation } from 'react-i18next';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#afcdf0' : '#afcdf0',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

export default function TableFormDialog({ handleClose, handleSuccess, areas }) {
  const { t: tCommon } = useTranslation('common');
  const { t: tPopup } = useTranslation('popup');
  const [data, setData] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [area, setArea] = useState(null);
  const [chair, setChair] = useState('');

  const { postAlertMessage } = useContext(AlertMessagesContext);

  const handleCreateTable = async () => {
    if (area === null) {
      postAlertMessage({ text: tPopup('Please select an area'), type: 'failed' });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: data.name,
        order: data.order,
        area_id: area.id,
        chairs: data.chairs,
      };
      await TableService.createTables(payload);
      postAlertMessage({ text: tPopup('Table created successfully'), type: 'success' });
      await handleSuccess();
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setLoading(false);
  };

  const onAddChairClicked = () => {
    data.chairs ||= [];
    data.chairs.push({ name: chair });
    setChair('');
  };

  return (
    <>
      <div className={styles.popUp}>
        <div className={styles.contentWrapper}>
          <div className={styles.headSec}>
            <h2 className={styles.editTitle}>{tCommon('Add Table')}</h2>
            <CloseIcon onClick={handleClose} />
          </div>
          <div className={styles.inputSec}>
            <Grid container className={styles.inputRow} alignItems="center">
              <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>Table Name</h3>
              </Grid>
              <Grid item xs={8} sm={8}>
                <TextField
                  label={tCommon('Table Name')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  type="text"
                  value={data.name || ''}
                  onChange={(e) => {
                    setData({ ...data, name: e.target.value });
                  }}
                />
              </Grid>
            </Grid>
            <Grid container className={styles.inputRow} alignItems="center">
              <Grid item xs={4} className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>Area</h3>
              </Grid>
              <Grid item xs={8}>
                <TextField
                  select
                  label={tCommon('Select Area')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={area?.id || ''}
                  onChange={(e) => {
                    const selectedArea = areas.find((a) => a.id === Number(e.target.value));
                    setArea(selectedArea);
                  }}
                >
                  {areas.map((a) => (
                    <MenuItem key={a?.id} value={a?.id}>
                      {a.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Grid container className={styles.inputRow} alignItems="center">
              <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>Order</h3>
              </Grid>
              <Grid item xs={8} sm={8}>
                <NumberField
                  label={tCommon('Order')}
                  value={data.order || ''}
                  onChange={(e) => {
                    setData({ ...data, order: Number(e.target.value) });
                  }}
                />
              </Grid >
            </Grid >
            {(data.chairs || []).map((chair, index) => (
              <Grid container className={styles.inputRow} alignItems="center">
                <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
                  <h6 className={styles.tableName}>{tCommon('Chair {{number}}', { number: index + 1 })}</h6>
                </Grid>
                <Grid item xs={6}>
                  <Item>{chair.name} </Item>
                </Grid>
                <Grid item xs={2} display="flex" justifyContent="center">
                  <IconButton edge="end" aria-label="delete">
                    <DeleteIcon
                      onClick={() => {
                        data.chairs.splice(index, 1);
                        setData({
                          ...data,
                          chairs: data.chairs,
                        });
                      }}
                    />
                  </IconButton>
                </Grid>
              </Grid>
            ))
            }
            <Grid container className={styles.inputRow} alignItems="center">
              <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
                <h6 className={styles.tableName}>{tCommon('New Chair')}</h6>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={tCommon('Chair Name')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={chair}
                  type="text"
                  onChange={(e) => {
                    setChair(e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={2} display="flex" justifyContent="center">
                <IconButton fullWidth edge="end" aria-label="add">
                  <LibraryAddIcon disabled={!data.chair} onClick={onAddChairClicked} />
                </IconButton>
              </Grid>
            </Grid>
            <DialogActions>
              <Button variant="contained" color="secondary" size="small" onClick={handleClose}>
                {tCommon('Close')}
              </Button>
              <Button
                variant="contained"
                size="small"
                style={{ backgroundColor: '#00a65a' }}
                disabled={!data.name || data.name === '' || isLoading}
                onClick={handleCreateTable}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                    {tCommon('Creating...')}
                  </>
                ) : (
                  tCommon('Create')
                )}
              </Button>
            </DialogActions>
          </div >
        </div >
      </div >
    </>
  );
}
